import React from 'react';
import FormField from '../shared/FormField';
import CountrySelect, { getCountryName } from '../shared/CountrySelect';
import USStateSelect from '../shared/USStateSelect';
import ChineseZIPCodeInput from '../shared/ChineseZIPCodeInput';
import ProvinceCitySelect from '../shared/ProvinceCitySelect';
import formDataStorage from '../../utils/formDataStorage';
import { Briefcase, GraduationCap, Plus, Trash2 } from 'lucide-react';

// 工作教育历史表单
const WorkEducation2Form = ({ data, onChange, fieldErrors = {} }) => {

  // 从formDataStorage读取数据并确保数据同步
  React.useEffect(() => {
    console.log('WorkEducation2Form useEffect - 初始化数据检查');
    const savedData = formDataStorage.loadFormData('work_education2');
    console.log('WorkEducation2Form useEffect - 已保存数据:', savedData);
    console.log('WorkEducation2Form useEffect - 当前props数据:', data);
    
    // 如果有保存的数据但当前data为空或部分字段缺失，则从数据库加载
    if (savedData && (!data || Object.keys(data).length === 0)) {
      console.log('WorkEducation2Form useEffect - 从数据库加载数据');
      Object.keys(savedData).forEach(key => {
        if (savedData[key] !== null && savedData[key] !== undefined) {
          onChange(key, savedData[key]);
        }
      });
    }
  }, [data, onChange]);

  // 月份映射
  const MONTHS = [
    { value: '1', label: '01月' },
    { value: '2', label: '02月' },
    { value: '3', label: '03月' },
    { value: '4', label: '04月' },
    { value: '5', label: '05月' },
    { value: '6', label: '06月' },
    { value: '7', label: '07月' },
    { value: '8', label: '08月' },
    { value: '9', label: '09月' },
    { value: '10', label: '10月' },
    { value: '11', label: '11月' },
    { value: '12', label: '12月' }
  ];

  // 获取指定年月的天数
  const getDaysInMonth = (year, month) => {
    if (!year || !month) return 31;
    return new Date(year, month, 0).getDate();
  };

  // 生成日期选项
  const generateDayOptions = (year, month) => {
    const maxDays = getDaysInMonth(year, month);
    return Array.from({ length: maxDays }, (_, i) => {
      const day = String(i + 1).padStart(2, '0');
      return { value: day, label: `${day}日` };
    });
  };

  // 验证日期范围
  const validateDateRange = (fromDate, toDate) => {
    if (!fromDate || !toDate) return true;
    if (!fromDate.year || !fromDate.month || !fromDate.day ||
        !toDate.year || !toDate.month || !toDate.day) return true;
    
    const startDate = new Date(fromDate.year, fromDate.month - 1, fromDate.day);
    const endDate = new Date(toDate.year, toDate.month - 1, toDate.day);
    
    return startDate <= endDate;
  };

  // 获取日期验证错误信息
  const getDateValidationError = (fromDate, toDate, type) => {
    if (!validateDateRange(fromDate, toDate)) {
      return type === 'employment' ? '工作开始日期不能晚于结束日期' : '就读开始日期不能晚于结束日期';
    }
    return null;
  };

  // 更新工作经历日期
  const updateEmploymentDate = (index, dateType, field, value) => {
    const currentEmployments = data.previous_employments || [];
    const currentEmployment = currentEmployments[index] || {};
    const currentDate = currentEmployment[dateType] || {};
    const newDate = { ...currentDate, [field]: value };
    
    // 如果修改了年或月，需要检查日期是否有效
    if (field === 'year' || field === 'month') {
      const maxDays = getDaysInMonth(newDate.year, newDate.month);
      if (newDate.day && parseInt(newDate.day) > maxDays) {
        newDate.day = String(maxDays);
      }
    }
    
    const newEmployment = { ...currentEmployment, [dateType]: newDate };
    const newEmployments = [...currentEmployments];
    newEmployments[index] = newEmployment;
    onChange('previous_employments', newEmployments);
  };

  // 更新工作经历信息
  const updateEmployment = (index, field, value) => {
    const currentEmployments = data.previous_employments || [];
    const currentEmployment = currentEmployments[index] || {};
    const newEmployment = { ...currentEmployment, [field]: value };
    const newEmployments = [...currentEmployments];
    newEmployments[index] = newEmployment;
    onChange('previous_employments', newEmployments);
  };

  // 批量更新工作经历字段，避免多次独立更新导致的数据回退
  const updateEmploymentFields = (index, updates) => {
    const currentEmployments = data.previous_employments || [];
    const newEmployments = currentEmployments.map((employment, i) => {
      if (i !== index) {
        return employment || {};
      }
      return { ...(employment || {}), ...updates };
    });
    onChange('previous_employments', newEmployments);
  };

  // 添加工作经历
  const addEmployment = () => {
    const currentEmployments = data.previous_employments || [];
    const newEmployment = {
      employer_name: '',
      employer_street_address1: '',
      employer_street_address2: '',
      employer_city: '',
      employer_state_province: '',
      employer_state_na: false,
      employer_postal_code: '',
      employer_postal_code_na: false,
      employer_country: '',
      employer_phone: '',
      job_title: '',
      supervisor_surname: '',
      supervisor_surname_na: false,
      supervisor_given_name: '',
      supervisor_given_name_na: false,
      employment_date_from: { day: '', month: '', year: '' },
      employment_date_to: { day: '', month: '', year: '' },
      describe_duties: ''
    };
    onChange('previous_employments', [...currentEmployments, newEmployment]);
  };

  // 删除工作经历
  const removeEmployment = (index) => {
    const currentEmployments = data.previous_employments || [];
    const newEmployments = currentEmployments.filter((_, i) => i !== index);
    onChange('previous_employments', newEmployments);
  };

  // 更新教育经历日期
  const updateEducationDate = (index, dateType, field, value) => {
    const currentEducations = data.previous_educations || [];
    const currentEducation = currentEducations[index] || {};
    const currentDate = currentEducation[dateType] || {};
    const newDate = { ...currentDate, [field]: value };
    
    // 如果修改了年或月，需要检查日期是否有效
    if (field === 'year' || field === 'month') {
      const maxDays = getDaysInMonth(newDate.year, newDate.month);
      if (newDate.day && parseInt(newDate.day) > maxDays) {
        newDate.day = String(maxDays);
      }
    }
    
    const newEducation = { ...currentEducation, [dateType]: newDate };
    const newEducations = [...currentEducations];
    newEducations[index] = newEducation;
    onChange('previous_educations', newEducations);
  };

  // 更新教育经历信息
  const updateEducation = (index, field, value) => {
    const currentEducations = data.previous_educations || [];
    const currentEducation = currentEducations[index] || {};
    const newEducation = { ...currentEducation, [field]: value };
    const newEducations = [...currentEducations];
    newEducations[index] = newEducation;
    onChange('previous_educations', newEducations);
  };

  // 批量更新教育经历字段
  const updateEducationFields = (index, updates) => {
    const currentEducations = data.previous_educations || [];
    const newEducations = currentEducations.map((education, i) => {
      if (i !== index) {
        return education || {};
      }
      return { ...(education || {}), ...updates };
    });
    onChange('previous_educations', newEducations);
  };

  // 添加教育经历
  const addEducation = () => {
    const currentEducations = data.previous_educations || [];
    const newEducation = {
      school_name: '',
      school_street_address1: '',
      school_street_address2: '',
      school_city: '',
      school_state_province: '',
      school_state_na: false,
      school_postal_code: '',
      school_postal_code_na: false,
      school_country: '',
      course_of_study: '',
      attendance_date_from: { day: '', month: '', year: '' },
      attendance_date_to: { day: '', month: '', year: '' }
    };
    onChange('previous_educations', [...currentEducations, newEducation]);
  };

  // 删除教育经历
  const removeEducation = (index) => {
    const currentEducations = data.previous_educations || [];
    const newEducations = currentEducations.filter((_, i) => i !== index);
    onChange('previous_educations', newEducations);
  };

  return (
    <div className="space-y-6">
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 p-6 rounded-lg">
        <h3 className="text-2xl font-bold text-gray-800 mb-6 flex items-center">
          <span className="w-8 h-8 bg-indigo-600 text-white rounded-full flex items-center justify-center mr-3 text-sm">📚</span>
          工作教育历史
        </h3>

        {/* 之前工作经历 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <Briefcase className="w-5 h-5 mr-2" />
              之前工作经历
            </h4>
            <div className="mb-4">
              <h5 className="text-md font-semibold text-gray-800 mb-3">您是否有之前的工作经历？</h5>
              <div className="flex gap-6 mb-3">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="previously_employed"
                    value="false"
                    checked={data.previously_employed === false}
                    onChange={(e) => {
                      onChange('previously_employed', false);
                      onChange('previous_employments', []);
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 我没有之前的工作经历</span>
                </label>
                
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="previously_employed"
                    value="true"
                    checked={data.previously_employed === true}
                    onChange={(e) => {
                      onChange('previously_employed', true);
                      if (!data.previous_employments || data.previous_employments.length === 0) {
                        addEmployment();
                      }
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 我有之前的工作经历</span>
                </label>
              </div>
              {data.previously_employed && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={addEmployment}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    添加工作经历
                  </button>
                </div>
              )}
            </div>
          </div>

          {data.previously_employed && (
            <div className="space-y-6">
              {(data.previous_employments || []).map((employment, index) => (
                <div key={index} className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-lg font-semibold text-blue-800">工作经历 {index + 1}</h5>
                    {(data.previous_employments || []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEmployment(index)}
                        className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                        title="删除此工作经历"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* 雇主信息 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        label="雇主名称"
                        value={employment.employer_name || ''}
                        onChange={(e) => updateEmployment(index, 'employer_name', e.target.value)}
                        placeholder="TEST COMPANY LTD"
                        required
                      />
                      <FormField
                        label="职位头衔"
                        value={employment.job_title || ''}
                        onChange={(e) => updateEmployment(index, 'job_title', e.target.value)}
                        placeholder="工程师"
                        required
                      />
                    </div>

                    {/* 雇主地址 */}
                    <div className="space-y-4">
                      <FormField
                        label="街道地址（第一行）"
                        value={employment.employer_street_address1 || ''}
                        onChange={(e) => updateEmployment(index, 'employer_street_address1', e.target.value)}
                        placeholder="123 Business Street"
                        required
                      />
                      <FormField
                        label="街道地址（第二行）"
                        value={employment.employer_street_address2 || ''}
                        onChange={(e) => updateEmployment(index, 'employer_street_address2', e.target.value)}
                        placeholder="Suite 456（可选）"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <CountrySelect
                            label="国家"
                            value={employment.employer_country || ''}
                            onChange={(value) => updateEmployment(index, 'employer_country', value)}
                            required
                          />
                        </div>
                      </div>
                      {(() => {
                        const country = employment.employer_country;
                        const isUSA = country === 'USA';
                        const isChineseRegion = ['CHN', 'CHIN', 'CN', 'MAC', 'HKG', 'TWN'].includes(country);

                        if (isChineseRegion) {
                          return (
                            <ProvinceCitySelect
                              provinceValue={employment.employer_state_province || ''}
                              cityValue={employment.employer_city || ''}
                              onProvinceChange={(value) => {
                                updateEmploymentFields(index, {
                                  employer_state_province: value,
                                  employer_city: '',
                                });
                              }}
                              onCityChange={(value) => updateEmployment(index, 'employer_city', value)}
                            />
                          );
                        }

                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {isUSA ? (
                              <USStateSelect
                                label="州"
                                value={employment.employer_state_province || ''}
                                onChange={(value) => updateEmployment(index, 'employer_state_province', value)}
                                required
                              />
                            ) : (
                              <FormField
                                label="州/省"
                                value={employment.employer_state_province || ''}
                                onChange={(e) => updateEmployment(index, 'employer_state_province', e.target.value)}
                                placeholder="State / Province"
                                required
                              />
                            )}
                            <FormField
                              label="城市"
                              value={employment.employer_city || ''}
                              onChange={(e) => updateEmployment(index, 'employer_city', e.target.value)}
                              placeholder="New York"
                              required
                            />
                          </div>
                        );
                      })()}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ChineseZIPCodeInput
                          label="邮政编码"
                          value={employment.employer_postal_code || ''}
                          onChange={(value) => updateEmployment(index, 'employer_postal_code', value)}
                          placeholder="100000"
                          required
                          countryCode={employment.employer_country}
                        />
                        <div />
                      </div>
                    </div>

                    {/* 联系信息 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        label="雇主电话"
                        type="tel"
                        value={employment.employer_phone || ''}
                        onChange={(e) => updateEmployment(index, 'employer_phone', e.target.value)}
                        placeholder="114514"
                        required
                      />
                    </div>

                    {/* 主管信息 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        label="主管姓氏"
                        value={employment.supervisor_surname || ''}
                        onChange={(e) => updateEmployment(index, 'supervisor_surname', e.target.value)}
                        placeholder="LI"
                        required
                      />
                      <FormField
                        label="主管名字"
                        value={employment.supervisor_given_name || ''}
                        onChange={(e) => updateEmployment(index, 'supervisor_given_name', e.target.value)}
                        placeholder="HUA"
                        required
                      />
                    </div>

                    {/* 工作日期 */}
                    <div className="space-y-4">
                      <h6 className="text-md font-semibold text-gray-700">工作日期</h6>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* 开始日期 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">开始日期</label>
                          <div className="grid grid-cols-3 gap-2">
                          <input
                            id={`employment_${index}_from_year`}
                            type="number"
                              min="1980"
                              max="2030"
                              placeholder="2010"
                              value={employment.employment_date_from?.year || ''}
                              onChange={(e) => updateEmploymentDate(index, 'employment_date_from', 'year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium text-sm"
                            />
                          <select
                            id={`employment_${index}_from_month`}
                            value={employment.employment_date_from?.month || ''}
                              onChange={(e) => updateEmploymentDate(index, 'employment_date_from', 'month', e.target.value)}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm"
                            >
                              <option value="">月</option>
                              {MONTHS.map(month => (
                                <option key={month.value} value={month.value}>{month.label.split(' ')[0]}</option>
                              ))}
                            </select>
                          <select
                            id={`employment_${index}_from_day`}
                            value={employment.employment_date_from?.day || ''}
                              onChange={(e) => updateEmploymentDate(index, 'employment_date_from', 'day', e.target.value)}
                              disabled={!employment.employment_date_from?.year || !employment.employment_date_from?.month}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <option value="">日</option>
                              {generateDayOptions(employment.employment_date_from?.year, employment.employment_date_from?.month).map(day => (
                                <option key={day.value} value={day.value}>{day.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* 结束日期 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">结束日期</label>
                          <div className="grid grid-cols-3 gap-2">
                          <input
                            id={`employment_${index}_to_year`}
                            type="number"
                              min="1980"
                              max="2030"
                              placeholder="2010"
                              value={employment.employment_date_to?.year || ''}
                              onChange={(e) => updateEmploymentDate(index, 'employment_date_to', 'year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium text-sm"
                            />
                          <select
                            id={`employment_${index}_to_month`}
                            value={employment.employment_date_to?.month || ''}
                              onChange={(e) => updateEmploymentDate(index, 'employment_date_to', 'month', e.target.value)}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm"
                            >
                              <option value="">月</option>
                              {MONTHS.map(month => (
                                <option key={month.value} value={month.value}>{month.label.split(' ')[0]}</option>
                              ))}
                            </select>
                          <select
                            id={`employment_${index}_to_day`}
                            value={employment.employment_date_to?.day || ''}
                              onChange={(e) => updateEmploymentDate(index, 'employment_date_to', 'day', e.target.value)}
                              disabled={!employment.employment_date_to?.year || !employment.employment_date_to?.month}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <option value="">日</option>
                              {generateDayOptions(employment.employment_date_to?.year, employment.employment_date_to?.month).map(day => (
                                <option key={day.value} value={day.value}>{day.label}</option>
                              ))}
                            </select>
                          </div>
                          {/* 日期验证错误提示 */}
                          {getDateValidationError(employment.employment_date_from, employment.employment_date_to, 'employment') && (
                            <div className="mt-2 text-sm text-red-600 flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {getDateValidationError(employment.employment_date_from, employment.employment_date_to, 'employment')}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* 工作职责描述 */}
                    {fieldErrors && fieldErrors[`#employment_${index}_to_year`] && (
                      <div className="mt-1 text-xs text-red-600">{fieldErrors[`#employment_${index}_to_year`]}</div>
                    )}
                    <FormField
                      label="工作职责描述"
                      type="textarea"
                      value={employment.describe_duties || ''}
                      onChange={(e) => updateEmployment(index, 'describe_duties', e.target.value)}
                      placeholder="请详细描述您在此职位的工作职责..."
                      required
                    />
                    <p className="text-xs text-amber-600 -mt-2">请用英文填写，建议简单描述主要工作内容即可</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 其他教育经历 */}
        <div className="bg-white p-6 rounded-lg shadow-sm mb-6">
          <div className="mb-4">
            <h4 className="text-lg font-semibold text-gray-800 flex items-center mb-4">
              <GraduationCap className="w-5 h-5 mr-2" />
              其他教育经历
            </h4>
            <div className="mb-4">
              <h5 className="text-md font-semibold text-gray-800 mb-3">您是否有其他教育经历？</h5>
              <div className="flex gap-6 mb-3">
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="other_education"
                    value="false"
                    checked={data.other_education === false}
                    onChange={(e) => {
                      onChange('other_education', false);
                      onChange('previous_educations', []);
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  />
                  <span className="text-base font-medium text-gray-700">❌ 否 - 我没有其他教育经历</span>
                </label>
                
                <label className="flex items-center cursor-pointer p-3 border-2 border-gray-200 rounded-lg hover:border-blue-400 hover:bg-blue-50 transition-all duration-200">
                  <input
                    type="radio"
                    name="other_education"
                    value="true"
                    checked={data.other_education === true}
                    onChange={(e) => {
                      onChange('other_education', true);
                      if (!data.previous_educations || data.previous_educations.length === 0) {
                        addEducation();
                      }
                    }}
                    className="mr-3 w-5 h-5 text-blue-600 border-gray-300 focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50"
                  />
                  <span className="text-base font-medium text-gray-700">✅ 是 - 我有其他教育经历</span>
                </label>
              </div>
              {data.other_education && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={addEducation}
                    className="flex items-center px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 text-sm font-medium shadow-sm hover:shadow-md"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    添加教育经历
                  </button>
                </div>
              )}
            </div>
          </div>

          {data.other_education && (
            <div className="space-y-6">
              {(data.previous_educations || []).map((education, index) => (
                <div key={index} className="p-6 bg-gradient-to-r from-gray-50 to-blue-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
                  <div className="flex items-center justify-between mb-4">
                    <h5 className="text-lg font-semibold text-blue-800">教育经历 {index + 1}</h5>
                    {(data.previous_educations || []).length > 1 && (
                      <button
                        type="button"
                        onClick={() => removeEducation(index)}
                        className="flex items-center justify-center w-8 h-8 bg-gradient-to-r from-red-500 to-red-600 text-white rounded-lg hover:from-red-600 hover:to-red-700 transition-all duration-200 shadow-sm hover:shadow-md"
                        title="删除此教育经历"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    )}
                  </div>

                  <div className="space-y-4">
                    {/* 学校信息 */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <FormField
                        label="学校名称"
                        value={education.school_name || ''}
                        onChange={(e) => updateEducation(index, 'school_name', e.target.value)}
                        placeholder="TEST SCHOOL"
                        required
                      />
                      <div>
                        <FormField
                          label="专业/课程"
                          value={education.course_of_study || ''}
                          onChange={(e) => updateEducation(index, 'course_of_study', e.target.value)}
                          placeholder="Academic / Computer Science"
                          required
                        />
                        <p className="text-xs text-amber-600 mt-1">提示：高中填写"Academic"，高中以上填写您的学科专业（如Computer Science）</p>
                      </div>
                    </div>

                    {/* 学校地址 */}
                    <div className="space-y-4">
                      <FormField
                        label="街道地址（第一行）"
                        value={education.school_street_address1 || ''}
                        onChange={(e) => updateEducation(index, 'school_street_address1', e.target.value)}
                        placeholder="123 Education Street"
                        required
                      />
                      <FormField
                        label="街道地址（第二行）"
                        value={education.school_street_address2 || ''}
                        onChange={(e) => updateEducation(index, 'school_street_address2', e.target.value)}
                        placeholder="Building A（可选）"
                      />
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div className="md:col-span-2">
                          <CountrySelect
                            label="国家"
                            value={education.school_country || ''}
                            onChange={(value) => updateEducation(index, 'school_country', value)}
                            required
                          />
                        </div>
                      </div>
                      {(() => {
                        const country = education.school_country;
                        const isUSA = country === 'USA';
                        const isChineseRegion = ['CHN', 'CHIN', 'CN', 'MAC', 'HKG', 'TWN'].includes(country);

                        if (isChineseRegion) {
                          return (
                            <ProvinceCitySelect
                              provinceValue={education.school_state_province || ''}
                              cityValue={education.school_city || ''}
                              onProvinceChange={(value) => {
                                updateEducationFields(index, {
                                  school_state_province: value,
                                  school_city: '',
                                });
                              }}
                              onCityChange={(value) => updateEducation(index, 'school_city', value)}
                            />
                          );
                        }

                        return (
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {isUSA ? (
                              <USStateSelect
                                label="州"
                                value={education.school_state_province || ''}
                                onChange={(value) => updateEducation(index, 'school_state_province', value)}
                                required
                              />
                            ) : (
                              <FormField
                                label="州/省"
                                value={education.school_state_province || ''}
                                onChange={(e) => updateEducation(index, 'school_state_province', e.target.value)}
                                placeholder="State / Province"
                                required
                              />
                            )}
                            <FormField
                              label="城市"
                              value={education.school_city || ''}
                              onChange={(e) => updateEducation(index, 'school_city', e.target.value)}
                              placeholder="New York"
                              required
                            />
                          </div>
                        );
                      })()}
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <ChineseZIPCodeInput
                          label="邮政编码"
                          value={education.school_postal_code || ''}
                          onChange={(value) => updateEducation(index, 'school_postal_code', value)}
                          placeholder="100000"
                          required
                          countryCode={education.school_country}
                        />
                        <div />
                      </div>
                    </div>

                    {/* 就读日期 */}
                    <div className="space-y-4">
                      <div>
                        <h6 className="text-md font-semibold text-gray-700">就读日期</h6>
                        <p className="text-xs text-amber-600">提示：填写大概日期即可，日期不准确没有关系</p>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* 开始日期 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">开始日期</label>
                          <div className="grid grid-cols-3 gap-2">
                          <input
                            id={`education_${index}_from_year`}
                            type="number"
                              min="1980"
                              max="2030"
                              placeholder="2006"
                              value={education.attendance_date_from?.year || ''}
                              onChange={(e) => updateEducationDate(index, 'attendance_date_from', 'year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium text-sm"
                            />
                          <select
                            id={`education_${index}_from_month`}
                            value={education.attendance_date_from?.month || ''}
                              onChange={(e) => updateEducationDate(index, 'attendance_date_from', 'month', e.target.value)}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm"
                            >
                              <option value="">月</option>
                              {MONTHS.map(month => (
                                <option key={month.value} value={month.value}>{month.label.split(' ')[0]}</option>
                              ))}
                            </select>
                          <select
                            id={`education_${index}_from_day`}
                            value={education.attendance_date_from?.day || ''}
                              onChange={(e) => updateEducationDate(index, 'attendance_date_from', 'day', e.target.value)}
                              disabled={!education.attendance_date_from?.year || !education.attendance_date_from?.month}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <option value="">日</option>
                              {generateDayOptions(education.attendance_date_from?.year, education.attendance_date_from?.month).map(day => (
                                <option key={day.value} value={day.value}>{day.label}</option>
                              ))}
                            </select>
                          </div>
                        </div>

                        {/* 结束日期 */}
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">结束日期</label>
                          <div className="grid grid-cols-3 gap-2">
                          <input
                            id={`education_${index}_to_year`}
                            type="number"
                              min="1980"
                              max="2030"
                              placeholder="2010"
                              value={education.attendance_date_to?.year || ''}
                              onChange={(e) => updateEducationDate(index, 'attendance_date_to', 'year', e.target.value)}
                              className="w-full h-10 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-center bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 font-medium text-sm"
                            />
                          <select
                            id={`education_${index}_to_month`}
                            value={education.attendance_date_to?.month || ''}
                              onChange={(e) => updateEducationDate(index, 'attendance_date_to', 'month', e.target.value)}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm"
                            >
                              <option value="">月</option>
                              {MONTHS.map(month => (
                                <option key={month.value} value={month.value}>{month.label.split(' ')[0]}</option>
                              ))}
                            </select>
                          <select
                            id={`education_${index}_to_day`}
                            value={education.attendance_date_to?.day || ''}
                              onChange={(e) => updateEducationDate(index, 'attendance_date_to', 'day', e.target.value)}
                              disabled={!education.attendance_date_to?.year || !education.attendance_date_to?.month}
                              className="w-full h-10 px-3 py-2 pr-8 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gradient-to-r from-white to-blue-50 shadow-sm hover:shadow-md transition-all duration-200 appearance-none cursor-pointer font-medium text-sm disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed"
                            >
                              <option value="">日</option>
                              {generateDayOptions(education.attendance_date_to?.year, education.attendance_date_to?.month).map(day => (
                                <option key={day.value} value={day.value}>{day.label}</option>
                              ))}
                            </select>
                          </div>
                          {/* 日期验证错误提示 */}
                          {getDateValidationError(education.attendance_date_from, education.attendance_date_to, 'education') && (
                            <div className="mt-2 text-sm text-red-600 flex items-center">
                              <svg className="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                              </svg>
                              {getDateValidationError(education.attendance_date_from, education.attendance_date_to, 'education')}
                            </div>
                          )}
                          {fieldErrors && fieldErrors[`#education_${index}_to_year`] && (
                            <div className="mt-1 text-xs text-red-600">{fieldErrors[`#education_${index}_to_year`]}</div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* 预览信息 */}
        {(data.previously_employed || data.other_education) && (
          <div className="mt-6 p-4 bg-gradient-to-r from-blue-50 to-blue-50 rounded-lg border-l-4 border-blue-400 shadow-sm">
            <h4 className="font-semibold text-blue-800 mb-2">信息预览</h4>
            <div className="text-sm text-blue-700 space-y-2">
              {data.previously_employed && (
                <div>
                  <p><strong>工作经历:</strong> {(data.previous_employments || []).length} 个</p>
                  {(data.previous_employments || []).map((emp, index) => (
                    <div key={index} className="ml-4 text-xs">
                      <p>• {emp.employer_name} - {emp.job_title}</p>
                      <p className="ml-4">  {emp.employer_city}, {getCountryName(emp.employer_country)}</p>
                    </div>
                  ))}
                </div>
              )}
              {data.other_education && (
                <div>
                  <p><strong>教育经历:</strong> {(data.previous_educations || []).length} 个</p>
                  {(data.previous_educations || []).map((edu, index) => (
                    <div key={index} className="ml-4 text-xs">
                      <p>• {edu.school_name} - {edu.course_of_study}</p>
                      <p className="ml-4">  {edu.school_city}, {getCountryName(edu.school_country)}</p>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WorkEducation2Form;
